"use client";

import { Button } from "@tracify/ui/button";
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { Textarea } from "@tracify/ui/textarea";
import type { UseChatHelpers } from "@ai-sdk/react";
import type { ChatMessage } from "@tracify/shared";
import { getTextFromMessage, isRuntimeContextText } from "@tracify/shared";

export type ChatMessageEditorProps = {
  message: ChatMessage;
  setMode: Dispatch<SetStateAction<"view" | "edit">>;
  setMessages: UseChatHelpers<ChatMessage>["setMessages"];
  regenerate: UseChatHelpers<ChatMessage>["regenerate"];
};

export function ChatMessageEditor({
  message,
  setMode,
  setMessages,
  regenerate,
}: ChatMessageEditorProps) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [draftContent, setDraftContent] = useState<string>(
    getTextFromMessage(message)
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${
        textareaRef.current.scrollHeight + 2
      }px`;
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      adjustHeight();
    }
  }, []);

  const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraftContent(event.target.value);
    adjustHeight();
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      <Textarea
        data-testid="message-editor"
        ref={textareaRef}
        className="bg-transparent outline-none overflow-hidden resize-none !text-base rounded-xl w-full"
        value={draftContent}
        onChange={handleInput}
      />

      <div className="flex flex-row gap-2 justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setMode("view");
          }}
        >
          Cancel
        </Button>
        <Button
          data-testid="message-editor-send-button"
          size="sm"
          disabled={isSubmitting}
          onClick={async () => {
            setIsSubmitting(true);

            setMessages((messages) => {
              const index = messages.findIndex((m) => m.id === message.id);

              if (index === -1) {
                return messages;
              }

              const preservedRuntimeContextParts = message.parts.filter(
                (part) =>
                  part.type === "text" && isRuntimeContextText(part.text ?? "")
              );
              const updatedMessage: ChatMessage = {
                ...message,
                parts: [
                  { type: "text", text: draftContent },
                  ...preservedRuntimeContextParts,
                ],
              };

              return [...messages.slice(0, index), updatedMessage];
            });

            setMode("view");
            await regenerate({ messageId: message.id });
          }}
        >
          {isSubmitting ? "Sending..." : "Send"}
        </Button>
      </div>
    </div>
  );
}
